'use client'

import { motion, useInView } from 'framer-motion'
import { useRef, useState } from 'react'
import { RouteLink } from '@/lib/router'
import type { DisplayProject } from '@/lib/portfolioDisplay'

export function PortfolioCard({
  study,
  index,
}: {
  study: DisplayProject
  index: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [isHovered, setIsHovered] = useState(false)
  const href = study.caseStudyPath || study.link
  const external = !study.caseStudyPath

  return (
    <motion.article
      ref={ref}
      className="group relative"
      initial={{ opacity: 0, y: 80 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 80 }}
      transition={{ duration: 1, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <RouteLink
        to={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noreferrer' : undefined}
        className="block"
        aria-label={
          study.caseStudyPath
            ? `${study.title} - read case study`
            : `${study.title} - view live site`
        }
      >
        <div className="relative mb-8 aspect-[16/10] overflow-hidden border border-border bg-muted/40">
          <img
            src={study.image}
            alt={`${study.title} - project screenshot`}
            className="h-full w-full object-contain object-top"
            loading={index === 0 ? 'eager' : 'lazy'}
          />

          <motion.div
            className="absolute top-8 right-8 flex h-14 w-14 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md backdrop-blur-sm md:top-12 md:right-12"
            animate={{ opacity: isHovered ? 1 : 0, rotate: isHovered ? 0 : -45 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17L17 7M17 7H7M17 7V17" />
            </svg>
          </motion.div>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-3">
            <span className="text-xs tracking-[0.3em] uppercase text-accent">{study.category}</span>
            <span className="text-xs tracking-[0.3em] uppercase text-muted-foreground">{study.year}</span>
          </div>
          {study.lab && study.category === 'Lab build' && (
            <span className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Prototype
            </span>
          )}
          <h3 className="font-serif text-3xl leading-tight text-foreground md:text-4xl lg:text-5xl">
            {study.title}
          </h3>
          <p className="mt-2 max-w-2xl font-serif text-lg italic text-muted-foreground">{study.subtitle}</p>
        </div>
      </RouteLink>

      <p className="mt-4 max-w-3xl font-serif text-lg leading-relaxed text-muted-foreground">
        {study.excerpt}
      </p>
      {study.caseStudyPath && (
        <p className="mt-3">
          <RouteLink
            to={study.caseStudyPath}
            className="text-sm uppercase tracking-[0.15em] text-foreground underline-offset-4 hover:text-accent hover:underline"
          >
            Read the case study
          </RouteLink>
        </p>
      )}
    </motion.article>
  )
}
